/**
 * Klient k API LoxLinku.
 *
 * Relacia je v cookie `httpOnly`, takze sa neposiela ziadny token - staci
 * `credentials: 'same-origin'`. Ziadny token v `localStorage`: pri XSS
 * diere by odisiel utocnikovi.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }

  /** Vyzaduje odpoved prihlasenie? */
  get needsLogin(): boolean {
    return this.status === 401;
  }
}

interface Poziadavka {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

/**
 * Chybova hlaska zo servera.
 *
 * Server posiela zrozumitelnu spravu bez technickych detailov; ak by
 * odpoved nebola JSON (napr. proxy vratila HTML), pouzije sa vseobecna.
 */
async function chybovaSprava(response: Response): Promise<string> {
  try {
    const telo: unknown = await response.json();
    if (telo !== null && typeof telo === 'object' && 'error' in telo) {
      const sprava = (telo as { error: unknown }).error;
      if (typeof sprava === 'string' && sprava.length > 0) return sprava;
    }
  } catch {
    // Odpoved nebola JSON; nizsie sa pouzije vseobecna hlaska.
  }
  return response.status >= 500
    ? 'Server odpovedal chybou. Skús to znova.'
    : 'Požiadavku sa nepodarilo spracovať.';
}

export async function api<T>(path: string, options: Poziadavka = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: options.body === undefined ? {} : { 'content-type': 'application/json' },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (!response.ok) throw new ApiError(response.status, await chybovaSprava(response));
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// --- tvary odpovedi -------------------------------------------------------

export type Role = 'admin' | 'viewer';

export interface Ucet {
  readonly id: string;
  readonly username: string;
  readonly role: Role;
  readonly lastLoginAt: string | null;
}

export interface Schopnost {
  readonly name: string;
  readonly access: 'read' | 'write' | 'readwrite';
  /**
   * Jednotka zo modelu zariadení, napr. `°C`; `null`, keď žiadnu nemá.
   *
   * Prichádza zo servera zámerne: rozhranie si zoznam jednotiek
   * nedrží, inak sa nová schopnosť v meraniach neukáže.
   */
  readonly unit?: string | null;
  /**
   * Skutočný rozsah tohto zariadenia, keď ho hlási.
   *
   * Žiarovka s laditeľnou bielou vie napríklad len 2200 až 4000 K
   * a všetko nad tým potichu oreže — bez tohto údaja to vyzerá ako
   * porucha.
   */
  readonly range?: { readonly min: number; readonly max: number };
  /**
   * Možné hodnoty, keď ich schopnosť má konečný zoznam (`Action`).
   *
   * Pri tlačidlách je to zoznam giest, ktoré zariadenie hlási. Rozhranie
   * Zigbee2MQTT je viazané len na slučku, takže toto je jediné miesto,
   * kde ich používateľ uvidí.
   */
  readonly values?: readonly string[];
}

export interface Dostupnost {
  readonly state: 'online' | 'offline' | 'unknown';
  readonly since: string;
  readonly reason?: string;
}

export interface Zariadenie {
  readonly id: string;
  readonly integrationId: string;
  readonly nativeId: string;
  readonly name: string;
  readonly manufacturer: string | null;
  readonly model: string | null;
  readonly roomId: string | null;
  readonly profileId: string | null;
  readonly capabilities: readonly Schopnost[];
  readonly availability: Dostupnost;
  /** Posledné známe hodnoty schopností, napr. `{ OnOff: true, Brightness: 40 }`. */
  readonly state: Readonly<Record<string, unknown>>;
  /** Kedy prišla posledná hodnota zo zariadenia; `null`, kým neprišla. */
  readonly lastMessageAt: string | null;
  /**
   * Posledné stlačenie tlačidla; `null`, kým žiadne nebolo.
   *
   * V `state` sa nenájde — akcia je udalosť a jej hodnota platí zlomok
   * sekundy, kým sa obrazovka obnovuje po sekundách.
   */
  readonly lastAction: { readonly value: string; readonly at: string } | null;
  readonly updatedAt: string;
}

/**
 * Rozpocet subscriptions natívneho MQTT klienta Loxone.
 *
 * Pocita ho server, nie rozhranie - pocet tem na zariadenie je vlastnost
 * menneho priestoru tem, nie zobrazenia.
 */
export interface LoxoneRozpocet {
  readonly deviceCount: number;
  readonly topics: number;
  readonly limit: number;
  readonly maxDevices: number;
  readonly fits: boolean;
  /** Koľko MQTT klientov treba v Loxone Config; každý na inú IP adresu. */
  readonly clientsNeeded: number;
}

export interface Integracia {
  readonly id: string;
  readonly name: string;
  readonly isSource: boolean;
  readonly isSink: boolean;
  readonly state: 'stopped' | 'starting' | 'running' | 'degraded' | 'failed';
  readonly detail: string;
  readonly deviceCount: number;
  /** Len pri MQTT; ine integracie tento limit nemaju. */
  readonly loxoneBudget?: LoxoneRozpocet;
}

export interface Miniserver {
  readonly id: string;
  readonly name: string;
  readonly host: string;
  readonly connected: boolean;
  readonly serialNumber: string;
  readonly configVersion: string | null;
  readonly lastModified?: string;
  readonly deviceCount: number;
  readonly lastError?: string;
}

export interface Mapovanie {
  readonly id: string;
  readonly deviceId: string;
  readonly miniserverId: string;
  readonly transport: 'udp-batch' | 'http-poll';
  readonly virtualInputName: string;
  readonly enabled: boolean;
  /**
   * Posledný paket, ktorý brána poslala do Miniservera.
   *
   * Diagnostika: keď sa hodnota v Loxone nemení, toto rozhodne medzi
   * „neposielame ju" a „Miniserver ju nespracuje".
   */
  readonly lastPayload: string | null;
  readonly lastSentAt: number | null;
  /**
   * Posledných pár paketov, od najnovšieho.
   *
   * Pri tlačidle posledný paket nestačí: impulz trvá 400 ms a hneď po ňom
   * ide návratový paket so samými nulami.
   */
  readonly recentPayloads: readonly { readonly payload: string; readonly at: number }[];
}

export interface KontrolaImportu {
  readonly miniserverId: string;
  readonly checked: boolean;
  readonly expected: readonly string[];
  readonly found: readonly string[];
  readonly missing: readonly string[];
  readonly ok: boolean;
}

export interface Sablona {
  readonly fileName: string;
  readonly xml: string;
  readonly objectName: string;
}

export interface Balik {
  /** Príkazy z Loxone; chýba pri zariadení, ktoré sa nedá ovládať. */
  readonly output: Sablona | null;
  /** Stavy do Loxone; chýba pri zariadení, ktoré nič nehlási. */
  readonly input: Sablona | null;
  readonly instructions: string;
  /**
   * Balíčky `.lxaddon` s tým istým obsahom.
   *
   * Inštalujú sa dvojklikom — bez kopírovania do priečinkov a bez
   * reštartu Loxone Config. Obsah je base64, lebo je to ZIP.
   */
  readonly addons: readonly { readonly fileName: string; readonly base64: string }[];
}

// --- volania --------------------------------------------------------------

export const auth = {
  status: () => api<{ setupPending: boolean }>('/api/v1/auth/status'),
  session: () => api<{ user: Ucet }>('/api/v1/auth/session'),
  login: (username: string, password: string) =>
    api<{ user: Ucet }>('/api/v1/auth/login', { method: 'POST', body: { username, password } }),
  logout: () => api<{ ok: true }>('/api/v1/auth/logout', { method: 'POST' }),
  setup: (token: string, username: string, password: string) =>
    api<{ user: Ucet }>('/api/v1/auth/setup', {
      method: 'POST',
      body: { token, username, password },
    }),
};

export interface UcetPrehlad extends Ucet {
  readonly createdAt: string;
}

export const account = {
  changePassword: (currentPassword: string, newPassword: string) =>
    api<{ ok: true }>('/api/v1/account/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),
  users: (signal?: AbortSignal) =>
    api<{ users: UcetPrehlad[] }>('/api/v1/users', signal ? { signal } : {}),
  createUser: (username: string, password: string, role: Role) =>
    api<{ user: UcetPrehlad }>('/api/v1/users', {
      method: 'POST',
      body: { username, password, role },
    }),
  setRole: (id: string, role: Role) =>
    api<{ user: UcetPrehlad }>(`/api/v1/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { role },
    }),
  removeUser: (id: string) =>
    api<{ ok: true }>(`/api/v1/users/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

/** Stav aktualizacie brany. Pocita ho server, rozhranie ho len ukazuje. */
export type StavAktualizacie =
  | { readonly kind: 'nenastavene' }
  | { readonly kind: 'bez-agenta' }
  /** Agent niečo píše, ale brána tomu nerozumie — nie je to to isté. */
  | { readonly kind: 'nezrozumitelny' }
  | {
      readonly kind: 'znamy';
      readonly beziacaVerzia: string;
      readonly dostupnaVerzia: string | null;
      readonly jeNovsia: boolean;
      readonly poslednaKontrolaAt: string | null;
      readonly poslednaChyba: string | null;
      readonly prebieha: {
        readonly id: string;
        readonly faza: string;
        readonly zacalaAt: string;
      } | null;
      readonly poslednyVysledok: {
        readonly id: string;
        readonly ok: boolean;
        readonly skoncilaAt: string;
        readonly verzia: string | null;
        readonly sprava: string;
      } | null;
    };

export const update = {
  status: (signal?: AbortSignal) =>
    api<{ update: StavAktualizacie }>('/api/v1/update', signal ? { signal } : {}),
  start: () => api<{ ok: true; id: string }>('/api/v1/update', { method: 'POST' }),
  check: () => api<{ ok: true; id: string }>('/api/v1/update/check', { method: 'POST' }),
};

export interface InfoZalohy {
  readonly createdAt: string;
  readonly appVersion: string;
  readonly files: readonly { readonly name: string; readonly size: number }[];
  readonly containsMasterKey: boolean;
}

export const backup = {
  status: (signal?: AbortSignal) =>
    api<{ available: boolean; pendingRestore: boolean }>(
      '/api/v1/backup/status',
      signal ? { signal } : {},
    ),
  /**
   * Vytvorenie zálohy.
   *
   * Je to POST a odpoveď je binárna: heslo k zálohe nesmie skončiť
   * v adrese ani v histórii prehliadača.
   */
  create: async (passphrase: string, includeMasterKey: boolean) => {
    const response = await fetch('/api/v1/backup', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ passphrase, includeMasterKey }),
    });
    if (!response.ok) {
      const telo: unknown = await response.json().catch(() => ({}));
      const sprava =
        telo !== null && typeof telo === 'object' && 'error' in telo
          ? String((telo as { error: unknown }).error)
          : 'Zálohu sa nepodarilo vytvoriť.';
      throw new ApiError(response.status, sprava);
    }
    const nazov =
      /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ??
      'loxlink.loxbak';
    return { blob: await response.blob(), fileName: nazov };
  },
  restore: (archive: ArrayBuffer, passphrase: string, confirm: boolean) =>
    api<{ backup: InfoZalohy; staged: boolean }>('/api/v1/backup/restore', {
      method: 'POST',
      body: {
        passphrase,
        confirm,
        archive: btoa(String.fromCharCode(...new Uint8Array(archive))),
      },
    }),
  cancelRestore: () => api<{ ok: true }>('/api/v1/backup/restore', { method: 'DELETE' }),
};

export const devices = {
  /**
   * Zmaže zariadenie z modelu.
   *
   * `forgottenByIntegration` hovorí, či ho zabudla aj integrácia. Keď
   * nie, pri najbližšom ohlásení sa vráti — a rozhranie to má povedať,
   * nie predstierať, že je preč navždy.
   */
  remove: (id: string) =>
    api<{ ok: true; forgottenByIntegration: boolean }>(
      `/api/v1/devices/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),
  list: (signal?: AbortSignal) =>
    api<{ devices: Zariadenie[] }>('/api/v1/devices', signal ? { signal } : {}),
  get: (id: string) => api<{ device: Zariadenie }>(`/api/v1/devices/${encodeURIComponent(id)}`),
  rename: (id: string, name: string) =>
    api<{ device: Zariadenie }>(`/api/v1/devices/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { name },
    }),
  command: (id: string, capability: string, value: unknown) =>
    api<{ ok: true }>(`/api/v1/devices/${encodeURIComponent(id)}/command`, {
      method: 'POST',
      body: { capability, value },
    }),
};

export interface TuyaZariadenie {
  readonly deviceId: string;
  readonly loxlinkId: string | undefined;
  readonly name: string | undefined;
  readonly ip: string | undefined;
  readonly version: string | undefined;
  readonly productKey: string | undefined;
  readonly profileId: string | undefined;
  readonly profileName: string | undefined;
  /** `true` = profil vznikol zo špecifikácie z Tuya IoT. */
  readonly profileDerived: boolean;
  readonly reportedDps: readonly {
    readonly code: string;
    readonly dp: string;
    readonly type: string;
  }[];
  readonly mappedDps: readonly string[];
  readonly hasLocalKey: boolean;
  readonly state: string;
  /** Hláška posledného zlyhania spojenia; `undefined`, keď žiadne nebolo. */
  readonly lastError?: string;
  readonly lastFailureAt?: number;
  /** Koľko pokusov o pripojenie zlyhalo za sebou. */
  readonly attempts?: number;
  /**
   * Prečo nie je pripojené.
   *
   * `auth-failed` = zariadenie odpovedalo, ale kľúč neberie (typicky po
   * prepárovaní v aplikácii Tuya). `unreachable` = nedalo sa k nemu
   * dostať vôbec.
   */
  readonly cause?: 'unreachable' | 'auth-failed' | 'stopped' | 'never-seen';
  readonly lastSeenAt: number | undefined;
}

export interface StavMostaZigbee {
  readonly online: boolean;
  readonly permitJoin: boolean;
  /** Koľko sekúnd párovania zostáva; `null`, keď nebeží. */
  readonly permitJoinRemainingSeconds: number | null;
  readonly version: string | undefined;
  readonly coordinator: string | undefined;
  readonly deviceCount: number;
  readonly baseTopic: string;
  readonly availabilityReported: boolean;
  /** Kanál 802.15.4 siete (11 až 26); `undefined`, keď ho most nehlási. */
  readonly channel?: number;
  readonly panId?: number;
  readonly extendedPanId?: string;
}

/**
 * Uzol siete, ktorý do modelu nepatrí — typicky opakovač.
 *
 * V Zigbee sieti je a prenáša cudziu prevádzku, ale nemá čo hlásiť ani
 * čo ovládať, takže do Loxone nejde.
 */
export interface InyUzolZigbee {
  readonly friendlyName: string;
  readonly ieeeAddress: string;
  readonly type: string | undefined;
  readonly model: string | undefined;
  readonly manufacturer: string | undefined;
  readonly unsupported: boolean;
}

/**
 * Topológia siete Zigbee.
 *
 * Zigbee je sieť, nie hviezda: koncové zariadenie hovorí s koordinátorom
 * cez routery. Ktorou cestou ide, sa inak nezistí.
 */
export interface UzolSiete {
  readonly ieeeAddress: string;
  readonly friendlyName: string;
  /** `Coordinator`, `Router`, `EndDevice`. */
  readonly type: string;
}

export interface VazbaSiete {
  readonly source: string;
  readonly target: string;
  readonly linkQuality: number;
}

export interface MapaSieteZigbee {
  readonly nodes: readonly UzolSiete[];
  readonly links: readonly VazbaSiete[];
  /** Kedy mapa vznikla (epoch ms). */
  readonly at: number;
}

/** Odkiaľ sa vzala dostupnosť: z mosta, odhadom, alebo nijako. */
export type ZdrojDostupnosti = 'most' | 'odhad' | 'ziadna';

export interface ZigbeeZariadenie extends Zariadenie {
  readonly availabilitySource: ZdrojDostupnosti;
  /**
   * Väzby zariadenia.
   *
   * Ovládač — tlačidlo, PIR — nehlási stav sám od seba: posiela Zigbee
   * príkaz tomu, s kým je zviazaný. Bez väzby na koordinátor sa o stlačení
   * ani o pohybe nikto nedozvie.
   */
  readonly bindings: readonly VazbaZigbee[];
  readonly ieeeAddress?: string;
  /** Stav firmvéru; `available` znamená, že čaká novšia verzia. */
  readonly firmware?: StavFirmveru;
  /**
   * Zdroj napájania podľa mosta, napr. `Battery`.
   *
   * Podstatný pri zmene kanála: zariadenie napájané zo siete broadcast
   * o zmene prevezme, spiace batériové ho často nestihne.
   */
  readonly powerSource?: string;
}

/**
 * Pripravenosť siete na Matter.
 *
 * Matter nezlyháva na kóde, ale na sieti — a zlyhá až na konci. Sonda
 * overí to, čo môže celý zámer zabiť, ešte pred prvým riadkom integrácie.
 */
export interface PripravenostMatter {
  readonly verdict: 'pripravene' | 's-vyhradami' | 'neprejde';
  readonly problems: readonly string[];
  readonly interfaces: readonly {
    readonly name: string;
    readonly ipv4: readonly string[];
    readonly ipv6: readonly string[];
    readonly ipv6LinkLocal: readonly string[];
  }[];
  readonly ipv6: boolean;
  readonly mdnsRespondersV4: number;
  readonly mdnsRespondersV6: number;
  readonly matterServices: readonly string[];
  readonly devicesOutsideSubnet: readonly string[];
  readonly at: string;
}

/** Zariadenie Matter tak, ako ho vidí rozhranie. */
export interface ZariadenieMatter {
  readonly nodeId: string;
  readonly deviceId: string;
  readonly online: boolean;
  readonly label: string | undefined;
  /**
   * Náš názov z registra — ten, ktorý sa dá premenovať.
   *
   * Matter sám nesie len `nodeLabel` a väčšina zariadení ho nenastavuje,
   * takže bez tohto zostane v zozname `peer1`.
   */
  readonly name: string | undefined;
}

/**
 * Spárované zariadenie, v ktorom brána nepozná žiadnu schopnosť.
 *
 * Do modelu nejde, ale v našej fabrike je — preto sa musí dať vypísať
 * a odobrať. Zoznam clusterov hovorí, čo by sa muselo doplniť.
 */
export interface NepodporovaneMatter {
  readonly nodeId: string;
  readonly label: string | undefined;
  readonly vendor: string | undefined;
  readonly product: string | undefined;
  readonly clusters: readonly string[];
}

export const matter = {
  prehlad: (signal?: AbortSignal) =>
    api<{
      enabled: boolean;
      devices: ZariadenieMatter[];
      unsupported: NepodporovaneMatter[];
      /** Bluetooth sa po spárovaní zasekne; ďalšie zariadenie chce nový proces. */
      bleNeedsRestart: boolean;
    }>('/api/v1/matter', signal ? { signal } : {}),
  /** Párovanie trvá desiatky sekúnd; odpoveď príde až po ňom. */
  commission: (pairingCode: string) =>
    api<{ device: Zariadenie }>('/api/v1/matter/commission', {
      method: 'POST',
      body: { pairingCode },
    }),
  /** Odobranie uzla z fabriky; funguje aj pre to, čo brána nepozná. */
  odober: (nodeId: string) =>
    api<{ ok: true }>(`/api/v1/matter/nodes/${encodeURIComponent(nodeId)}`, {
      method: 'DELETE',
    }),
};

export const system = {
  /**
   * Reštart brány.
   *
   * Existuje kvôli jedinej veci: Bluetooth sa po spárovaní zasekne
   * a v tom istom procese sa znova rozbehnúť nedá. Nový proces vráti
   * Docker sám.
   */
  restart: () => api<{ ok: true }>('/api/v1/system/restart', { method: 'POST' }),
};

export interface NastaveniaPocasia {
  readonly latitude: number;
  readonly longitude: number;
  readonly placeName: string;
  readonly hourlyHorizons: readonly number[];
  readonly dailyDays: readonly number[];
  readonly lastFetchAt: string | null;
}

export interface NajdenaObec {
  readonly name: string;
  readonly country: string;
  readonly admin: string | null;
  readonly latitude: number;
  readonly longitude: number;
}

export interface StavIntegracie {
  readonly state: string;
  readonly detail: string;
  readonly deviceCount: number;
}

export const weather = {
  prehlad: (signal?: AbortSignal) =>
    api<{ settings: NastaveniaPocasia | null; health: StavIntegracie }>(
      '/api/v1/weather',
      signal ? { signal } : {},
    ),
  uloz: (nastavenia: {
    latitude: number;
    longitude: number;
    placeName: string;
    hourlyHorizons: number[];
    dailyDays: number[];
  }) =>
    api<{ settings: NastaveniaPocasia; health: StavIntegracie }>('/api/v1/weather', {
      method: 'PUT',
      body: nastavenia,
    }),
  zabudni: () =>
    api<{ settings: null; health: StavIntegracie }>('/api/v1/weather', { method: 'DELETE' }),
  /** POST, aby názov obce neskončil v prístupovom logu ani v histórii. */
  hladaj: (q: string) =>
    api<{ places: NajdenaObec[] }>('/api/v1/weather/geocode', { method: 'POST', body: { q } }),
  obnov: () =>
    api<{ refresh: { fetchedAt: string; devices: number } }>('/api/v1/weather/refresh', {
      method: 'POST',
    }),
};

export const diagnostics = {
  /** Meranie, nie čítanie stavu — trvá pár sekúnd. */
  matter: () =>
    api<{ readiness: PripravenostMatter }>('/api/v1/diagnostics/matter', { method: 'POST' }),
};

export interface StavFirmveru {
  readonly state: 'idle' | 'available' | 'updating';
  readonly installedVersion?: number;
  readonly latestVersion?: number;
  /** Percentá, len počas aktualizácie. */
  readonly progress?: number;
  /** Odhad zostávajúceho času v sekundách, len počas aktualizácie. */
  readonly remaining?: number;
}

export interface VysledokFirmveru {
  readonly friendlyName: string;
  readonly ok: boolean;
  readonly error?: string;
  readonly at: string;
}

export interface VazbaZigbee {
  readonly cluster: string;
  readonly target: string;
}

/** Kvalita spojov Zigbee, spočítaná serverom z mapy siete. */
export interface KvalitaUzlaZigbee {
  readonly ieeeAddress: string;
  readonly friendlyName: string;
  readonly type: string;
  /** Najlepšie LQI uzla; `undefined` = v mape nemá žiadnu väzbu. */
  readonly najlepsieLqi?: number;
  readonly lqiNaKoordinator?: number;
  readonly kvalita: 'dobra' | 'slaba' | 'kriticka' | 'neznama';
}

export interface KvalitaSieteZigbee {
  readonly uzly: readonly KvalitaUzlaZigbee[];
  readonly dobrych: number;
  readonly slabych: number;
  readonly kritickych: number;
  readonly bezVazby: number;
  readonly at: number;
}

/*
 * Radia (Zigbee a Thread).
 *
 * Posudky kanalov pocita SERVER, nie tato obrazovka: su to pravidla
 * pasma 2,4 GHz a maju vlastne testy. Webove rozhranie je samostatny
 * balik bez zavislosti na `@loxlink/*`, takze keby si ich pocitalo samo,
 * vznikla by druha, netestovana kopia tej istej matematiky.
 */

export type SluzbaRadia = 'zigbee2mqtt' | 'otbr';

export interface PosudokKanala {
  readonly kanal: number;
  /** Bežné kanály Wi-Fi, ktoré sa s ním prekrývajú. */
  readonly rusiaceWifi: readonly number[];
  readonly ticheKanaly: readonly number[];
}

export interface SeriovyKlucRadia {
  readonly nazov: string;
  /** Uzol, na ktorý názov ukazuje, napr. `/dev/ttyACM0`. */
  readonly uzol: string | null;
  /** Ktorá služba ho používa; `null` znamená voľný kľúč. */
  readonly pouziva: SluzbaRadia | null;
}

export interface DatasetThreadu {
  readonly networkName: string | undefined;
  readonly channel: number | undefined;
  readonly panId: number | undefined;
  readonly extPanId: string | undefined;
  readonly meshLocalPrefix: string | undefined;
}

export type StavThreadu =
  | { readonly kind: 'nenastavene'; readonly posudok: PosudokKanala | null }
  | { readonly kind: 'nedostupny'; readonly chyba: string; readonly posudok: PosudokKanala | null }
  | {
      readonly kind: 'znamy';
      readonly stav: 'disabled' | 'detached' | 'child' | 'router' | 'leader';
      readonly dataset: DatasetThreadu | null;
      readonly verziaRadia: string | undefined;
      readonly posudok: PosudokKanala | null;
    };

export type StavAgentaRadii =
  | { readonly kind: 'nenastavene' }
  | { readonly kind: 'bez-agenta' }
  | { readonly kind: 'nezrozumitelny' }
  | {
      readonly kind: 'znamy';
      readonly nastavene: { readonly zigbee2mqtt: string | null; readonly otbr: string | null };
      /** Rozhranie do domácej siete, ktoré používa border router. */
      readonly infraIf: string | null;
      readonly rozhrania: readonly string[];
      readonly prebieha: {
        readonly id: string;
        readonly sluzba: string;
        readonly zacalaAt: string;
      } | null;
      readonly poslednyVysledok: {
        readonly id: string;
        readonly ok: boolean;
        readonly sluzba: string;
        readonly zariadenie: string | null;
        readonly skoncilaAt: string;
        readonly sprava: string;
      } | null;
    };

export interface VarovanieRadii {
  readonly kod: 'kluc-chyba' | 'kanaly-blizko' | 'wifi-prekryv' | 'bez-agenta' | 'infra-slucka';
  readonly sluzba?: SluzbaRadia;
  readonly rozhranie?: string;
  readonly zariadenie?: string;
  readonly kanal?: number;
  readonly kanaly?: readonly number[];
}

export interface PrehladRadii {
  readonly kluce: readonly SeriovyKlucRadia[];
  readonly zigbee: {
    readonly online: boolean;
    readonly coordinator: string | null;
    readonly serialPort: string | null;
    readonly serialBaudrate: number | null;
    readonly channel: number | null;
    readonly panId: number | null;
    readonly posudok: PosudokKanala | null;
  };
  readonly thread: StavThreadu;
  readonly agent: StavAgentaRadii;
  /** Sieťové rozhrania brány; z nich sa vyberá to pre border router. */
  readonly rozhrania: readonly string[];
  /**
   * Je posledný výsledok prepnutia ešte správa, alebo už história?
   *
   * Agent výsledok zapíše a sám ho nezahodí, takže staré zlyhanie by
   * inak hlásilo problém, ktorý už neexistuje.
   */
  readonly vysledokSviezi: boolean;
  readonly varovania: readonly VarovanieRadii[];
}

export interface VysledokZmenyThreadu {
  readonly ok: true;
  /** Po koľkých ms sieť zmenu prevezme; 0 znamená hneď. */
  readonly odkladMs?: number;
  /** Leader neodpovedal — výsledok je neznámy, nie zlyhaný. */
  readonly nezname?: boolean;
}

/**
 * Rozhrania, ktoré bežia vedľa brány.
 *
 * Názvy, popisy a rady sú v obrazovke — server dodáva fakty: port,
 * adresu, príkaz na tunel a to, či tam niečo počúva.
 */
export interface ExterneRozhranie {
  readonly id: 'zigbee2mqtt' | 'otbr';
  readonly port: number;
  readonly url: string;
  readonly tunel: string;
  readonly bezi: boolean;
}

export const rozhrania = {
  zoznam: (signal?: AbortSignal) =>
    api<{ rozhrania: readonly ExterneRozhranie[] }>('/api/v1/rozhrania', signal ? { signal } : {}),
};

/** Obsadenosť jedného kanála 802.15.4; `null` znamená nezmerané. */
export interface ObsadenostKanala {
  readonly kanal: number;
  readonly vzorky: readonly number[];
  readonly najhorsie: number | null;
  readonly priemer: number | null;
}

export interface OdporucenyKanalMeranim {
  readonly kanal: number;
  readonly najhorsie: number;
  readonly rusiaceWifi: readonly number[];
}

/**
 * Odpoveď na žiadosť o meranie.
 *
 * Je dvojfázová: border router hľadá cieľ merania vo svojej zbierke
 * zariadení a tú naplní až úloha prehľadania siete. Keď ešte naplnená
 * nie je, vráti sa `objavovanie` — a po jeho skončení si rozhranie
 * vyžiada meranie znova.
 */
export interface ZadaneMeranie {
  readonly faza: 'objavovanie' | 'meranie';
  readonly id: string;
  /** Adresa uzla, ktorý meria; len vo fáze `meranie`. */
  readonly uzol?: string;
}

export interface StavMerania {
  readonly stav: 'pending' | 'active' | 'completed' | 'stopped' | 'failed' | 'neznamy';
  readonly hotove: boolean;
  readonly obsadenost?: readonly ObsadenostKanala[];
  readonly odporucenie?: {
    readonly zigbee: OdporucenyKanalMeranim | null;
    readonly thread: OdporucenyKanalMeranim | null;
    readonly nezmerane: readonly number[];
    /** Rozdiel medzi najtichším a najhlučnejším zmeraným kanálom v dB. */
    readonly rozptylDb: number | null;
    /**
     * Je rozdiel dosť veľký, aby sa podľa neho dalo rozhodovať?
     *
     * Rozptyl merania sú jednotky decibelov. Keď je rozdiel v nich,
     * najtichší kanál je stále fakt — ale prepínať sieť kvôli nemu
     * nemá dôvod.
     */
    readonly rozdielVyznamny: boolean;
  };
}

/** Rola uzla, ako ju hlási samo zariadenie. */
export type RolaUzlaThreadu = 'router' | 'reed' | 'koncove' | 'spiace' | 'neznama';

/** Uzol mapy siete Thread. */
export interface UzolMapyThreadu {
  /** Číslo uzla v Matteri, alebo adresa, keď zariadenie nie je naše. */
  readonly id: string;
  /** Náš názov; `null` pri bráne a pri cudzom uzle. */
  readonly name: string | null;
  readonly nodeId: string | null;
  readonly adresa: string | null;
  readonly jeBrana: boolean;
  readonly rola: RolaUzlaThreadu;
  readonly online: boolean;
}

/** Spojenie so zmeranou kvalitou; `lqi` je stupnica 0 až 3. */
export interface SpojMapyThreadu {
  readonly z: string;
  readonly do: string;
  readonly lqi: number | null;
  readonly rssi: number | null;
}

export interface MapaThreadu {
  readonly uzly: readonly UzolMapyThreadu[];
  readonly spoje: readonly SpojMapyThreadu[];
  /** Uzly, ktoré nehlásia žiadneho suseda — teda bez čiary. */
  readonly bezSpoja: readonly string[];
}

export const radia = {
  prehlad: (signal?: AbortSignal) => api<PrehladRadii>('/api/v1/radia', signal ? { signal } : {}),
  /**
   * Prepnutie rádia. Odpoveď je 202: vykoná to agent na hostiteľovi
   * a služba sa znovu vytvorí až za chvíľu.
   */
  prepni: (sluzba: SluzbaRadia, zariadenie: string) =>
    api<{ ok: true; id: string }>(`/api/v1/radia/${sluzba}/zariadenie`, {
      method: 'POST',
      body: { zariadenie },
    }),
  /**
   * Založenie siete Thread alebo zmena jej kanála.
   *
   * `potvrdeneBlizko` je vedomé potvrdenie, že kanál smie byť bližšie
   * než tri kanály od Zigbee. Bez neho to server odmietne.
   */
  threadSiet: (vstup: {
    readonly kanal: number;
    readonly nazov?: string;
    readonly potvrdeneBlizko?: boolean;
  }) => api<VysledokZmenyThreadu>('/api/v1/radia/thread/siet', { method: 'POST', body: vstup }),
  /** Rozhranie do domácej siete pre border router. */
  threadRozhranie: (rozhranie: string) =>
    api<{ ok: true; id: string }>('/api/v1/radia/thread/rozhranie', {
      method: 'POST',
      body: { rozhranie },
    }),
  threadStav: (stav: 'enable' | 'disable') =>
    api<{ ok: true }>('/api/v1/radia/thread/stav', { method: 'POST', body: { stav } }),
  /**
   * Spustí meranie obsadenosti pásma.
   *
   * Čo sa meria, určuje server — celé pásmo a vlastná adresa rádia.
   * Odtiaľ sa nič neposiela, aby sa meranie nedalo poslať cudziemu uzlu.
   *
   * Meranie drží rolu commissionera a trvá niekoľko sekúnd; na výsledok
   * sa nečaká, vyzdvihne sa podľa čísla úlohy.
   */
  spustMeranie: () => api<ZadaneMeranie>('/api/v1/radia/thread/meranie', { method: 'POST' }),
  /** Mapa siete Thread; čítanie, do siete nezasahuje. */
  mapaThreadu: (signal?: AbortSignal) =>
    api<MapaThreadu>('/api/v1/radia/thread/mapa', signal ? { signal } : {}),
  /** Prehľadanie siete, ktoré naplní zbierku zariadení border routera. */
  prehladajSiet: () => api<{ id: string }>('/api/v1/radia/thread/prehladanie', { method: 'POST' }),
  meranie: (id: string, signal?: AbortSignal) =>
    api<StavMerania>(
      `/api/v1/radia/thread/meranie/${encodeURIComponent(id)}`,
      signal ? { signal } : {},
    ),
};

export const zigbee = {
  prehlad: (signal?: AbortSignal) =>
    api<{
      bridge: StavMostaZigbee;
      posudok: PosudokKanala | null;
      devices: ZigbeeZariadenie[];
      otherNodes: InyUzolZigbee[];
      networkMap: MapaSieteZigbee | null;
      linkQuality: KvalitaSieteZigbee | null;
      networkMapRunning: boolean;
      networkMapError: string | null;
      lastFirmwareResult: VysledokFirmveru | null;
    }>('/api/v1/zigbee', signal ? { signal } : {}),
  enableAvailability: () => api<{ ok: true }>('/api/v1/zigbee/availability', { method: 'POST' }),
  /**
   * Zmena kanála Zigbee siete.
   *
   * Odpoveď 202 znamená „zapísané": most sa reštartuje a až potom
   * rozošle zmenu po sieti. Kým to dobehne, sieť nekomunikuje.
   */
  zmenKanal: (kanal: number, potvrdeneBlizko = false) =>
    api<{ ok: true }>('/api/v1/zigbee/kanal', {
      method: 'POST',
      body: potvrdeneBlizko ? { kanal, potvrdeneBlizko } : { kanal },
    }),
  /**
   * Premenovanie uzla, ktorý nie je zariadením v modeli — typicky
   * opakovača. Robí to most; jeho názov je jediný, ktorý taký uzol má.
   */
  premenujUzol: (from: string, to: string) =>
    api<{ ok: true }>('/api/v1/zigbee/nodes/rename', { method: 'POST', body: { from, to } }),
  /** Vyžiada zmapovanie siete; výsledok príde do prehľadu neskôr. */
  mapNetwork: () => api<{ ok: true }>('/api/v1/zigbee/networkmap', { method: 'POST' }),
  /** Kontrola novšieho firmvéru pre jedno zariadenie; je rýchla. */
  checkFirmware: (ieeeAddress: string) =>
    api<{ check: { ok: boolean; error?: string } }>('/api/v1/zigbee/firmware/check', {
      method: 'POST',
      body: { ieeeAddress },
    }),
  /** Spustenie aktualizácie; beží ďalej aj po odpovedi, desiatky minút. */
  updateFirmware: (ieeeAddress: string) =>
    api<{ started: true }>('/api/v1/zigbee/firmware/update', {
      method: 'POST',
      body: { ieeeAddress },
    }),
  /** Väzba zariadenia na koordinátor; batériové musí byť pri tom hore. */
  bind: (ieeeAddress: string) =>
    api<{ bind: { ok: boolean; error?: string } }>('/api/v1/zigbee/bind', {
      method: 'POST',
      body: { ieeeAddress },
    }),
  permitJoin: (seconds: number) =>
    api<{ ok: true; seconds: number }>('/api/v1/zigbee/permit-join', {
      method: 'POST',
      body: { seconds },
    }),
};

export interface StavDiscovery {
  readonly running: boolean;
  readonly active: boolean;
  readonly boundPorts: readonly number[];
  readonly interfaces: readonly { readonly address: string; readonly broadcast: string }[];
  readonly receivedCount: number;
  readonly malformedCount: number;
  readonly solicitCount: number;
  /** Vlastné výzvy, ktoré sa vrátili späť na port 7000. */
  readonly ownProbeCount: number;
  readonly lastPacketAt: number | undefined;
  readonly lastError: string | undefined;
  /** Kto v sieti vysiela, podľa adresy. */
  readonly sources: readonly ZdrojPaketov[];
}

export interface ZdrojPaketov {
  readonly ip: string;
  readonly packets: number;
  readonly malformed: number;
  readonly lastAt: number;
  readonly gwId: string | undefined;
}

export interface VysledokSynchronizacie {
  readonly total: number;
  readonly added: number;
  readonly updated: number;
  readonly withoutKey: number;
  readonly devices: readonly {
    readonly deviceId: string;
    readonly name: string;
    readonly hasLocalKey: boolean;
    readonly online: boolean;
  }[];
}

export type TuyaRegion = 'eu' | 'eu-w' | 'us' | 'us-e' | 'cn' | 'in' | 'sg';

/** Uložené nastavenia Tuya IoT. Tajomstvo sa NEVRACIA. */
export interface NastaveniaTuyaCloud {
  readonly configured: boolean;
  readonly accessId?: string;
  readonly region?: TuyaRegion;
  readonly autoFetch: boolean;
  readonly hasSecret: boolean;
  readonly lastSyncAt: string | null;
}

export interface VysledokScanu {
  readonly scannedHosts: number;
  readonly subnets: readonly string[];
  readonly found: readonly string[];
  readonly skipped: readonly string[];
}

export interface NoveTuyaZariadenie {
  readonly deviceId: string;
  readonly name?: string;
  readonly localKey: string;
  readonly ip?: string;
  readonly version?: '3.1' | '3.2' | '3.3' | '3.4' | '3.5';
}

/** Údaje k projektu Tuya IoT; všetky voliteľné, keď je prístup uložený. */
export interface PrihlasenieTuyaCloud {
  readonly accessId?: string;
  readonly accessSecret?: string;
  readonly region?: TuyaRegion;
  readonly remember?: boolean;
  readonly autoFetch?: boolean;
}

/** Dátové centrá API mobilnej aplikácie; sú iné než pri Tuya IoT. */
export type TuyaAppRegion = 'eu' | 'us' | 'cn' | 'in';

/** Uložený účet aplikácie Tuya. Tajomstvo ani heslo sa NEVRACAJÚ. */
export interface UcetTuyaApp {
  readonly id: string;
  readonly label: string;
  readonly clientId: string;
  readonly region: TuyaAppRegion;
  readonly email: string;
  /** Identifikátor aplikácie pri Tuya: `tuya` alebo `smart_life`. */
  readonly ttid: string;
  /** Dopĺňa brána kľúče z tohto účtu sama? */
  readonly autoFetch: boolean;
  readonly lastSyncAt: string | null;
}

/** Nový účet; tajomstvo a heslo idú na server a späť sa nevracajú. */
export interface NovyUcetTuyaApp {
  readonly label: string;
  readonly clientId: string;
  readonly secret: string;
  readonly region: TuyaAppRegion;
  readonly email: string;
  readonly password: string;
  /** Prázdne znamená `tuya`; Smart Life potrebuje `smart_life`. */
  readonly ttid?: string;
}

/** Porovnanie účtu s bránou. Kľúče v ňom NIE SÚ. */
export interface VysledokSynchronizacieUctu {
  readonly accountId: string;
  readonly total: number;
  readonly known: number;
  readonly newDevices: readonly {
    readonly deviceId: string;
    readonly name: string;
    readonly category: string | undefined;
  }[];
  readonly changedKeys: readonly { readonly deviceId: string; readonly name: string }[];
  /** Známe zariadenia, ktorým kľúč chýba; doplnením sa nič neprepíše. */
  readonly missingKeys: readonly { readonly deviceId: string; readonly name: string }[];
}

/** Účet Tuya prihlásený QR kódom. Tokeny sa NEVRACAJÚ. */
export interface UcetTuyaQr {
  readonly id: string;
  readonly label: string;
  readonly userCode: string;
  readonly uid: string;
  readonly endpoint: string;
  readonly terminalId: string;
  readonly expiresAt: number;
  /** Dopĺňa brána kľúče z tohto účtu sama? */
  readonly autoFetch: boolean;
  readonly lastSyncAt: string | null;
}

/** Začiatok prihlásenia: obrázok QR kódu aj jeho obsah. */
export interface ZaciatokPrihlaseniaQr {
  readonly token: string;
  readonly qrObsah: string;
  /** Obrázok ako `data:` adresa; vkladá sa priamo do `img`. */
  readonly qr: string;
}

export const tuya = {
  /**
   * Vydanie lokálneho kľúča jedného zariadenia.
   *
   * Kľúč je plnohodnotný prístup k zariadeniu — POST preto, aby neskončil
   * v prístupovom logu ani v histórii prehliadača, a každé vydanie ide
   * do audit logu.
   */
  odhalKluc: (deviceId: string) =>
    api<{ localKey: string }>(`/api/v1/tuya/devices/${encodeURIComponent(deviceId)}/key/reveal`, {
      method: 'POST',
    }),
  /** Export všetkých uložených kľúčov ako CSV; poistka proti strate cloudu. */
  exportKlucov: () =>
    api<{ fileName: string; csv: string; count: number }>('/api/v1/tuya/keys/export', {
      method: 'POST',
    }),
  devices: (signal?: AbortSignal) =>
    api<{ devices: TuyaZariadenie[] }>('/api/v1/tuya/devices', signal ? { signal } : {}),
  discovery: (signal?: AbortSignal) =>
    api<{ discovery: StavDiscovery }>('/api/v1/tuya/discovery', signal ? { signal } : {}),
  solicit: () => api<{ ok: true }>('/api/v1/tuya/discovery/solicit', { method: 'POST' }),
  scan: () => api<{ scan: VysledokScanu }>('/api/v1/tuya/scan', { method: 'POST' }),
  cloudSettings: (signal?: AbortSignal) =>
    api<{ cloud: NastaveniaTuyaCloud }>('/api/v1/tuya/cloud', signal ? { signal } : {}),
  setAutoFetch: (autoFetch: boolean) =>
    api<{ cloud: NastaveniaTuyaCloud }>('/api/v1/tuya/cloud', {
      method: 'PATCH',
      body: { autoFetch },
    }),
  forgetCloud: () =>
    api<{ cloud: NastaveniaTuyaCloud }>('/api/v1/tuya/cloud', { method: 'DELETE' }),
  /**
   * Access Secret ide na server; späť sa NEVRACIA nikdy.
   *
   * Prázdne telo znamená „použi uložený prístup" — inak by si ho
   * používateľ prepisoval pri každom novom zariadení.
   */
  cloudSync: (vstup: PrihlasenieTuyaCloud = {}) =>
    api<{ sync: VysledokSynchronizacie }>('/api/v1/tuya/cloud/sync', {
      method: 'POST',
      body: vstup,
    }),
  add: (input: NoveTuyaZariadenie) =>
    api<{ device: TuyaZariadenie }>('/api/v1/tuya/devices', { method: 'POST', body: input }),
  remove: (deviceId: string) =>
    api<{ ok: true }>(`/api/v1/tuya/devices/${encodeURIComponent(deviceId)}`, {
      method: 'DELETE',
    }),
  /** Kľúč sa posiela len tam; späť sa nikdy nevracia. */
  setKey: (deviceId: string, localKey: string) =>
    api<{ ok: true }>(`/api/v1/tuya/devices/${encodeURIComponent(deviceId)}/key`, {
      method: 'POST',
      body: { localKey },
    }),
  forgetKey: (deviceId: string) =>
    api<{ ok: true }>(`/api/v1/tuya/devices/${encodeURIComponent(deviceId)}/key`, {
      method: 'DELETE',
    }),
  /** Účty prihlásené QR kódom; bez tokenov. */
  qrAccounts: (signal?: AbortSignal) =>
    api<{ accounts: UcetTuyaQr[] }>('/api/v1/tuya/sharing/accounts', signal ? { signal } : {}),
  startQrLogin: (label: string, userCode: string) =>
    api<ZaciatokPrihlaseniaQr>('/api/v1/tuya/sharing/login/start', {
      method: 'POST',
      body: { label, userCode },
    }),
  /** 409 znamená „zatiaľ nenaskenované" — treba sa spýtať znova. */
  finishQrLogin: (label: string, userCode: string, token: string) =>
    api<{ account: UcetTuyaQr }>('/api/v1/tuya/sharing/login/finish', {
      method: 'POST',
      body: { label, userCode, token },
    }),
  /** Zapnutie a vypnutie automatického dopĺňania kľúčov z účtu. */
  setQrAutoFetch: (id: string, autoFetch: boolean) =>
    api<{ ok: true }>(`/api/v1/tuya/sharing/accounts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { autoFetch },
    }),
  setAppAutoFetch: (id: string, autoFetch: boolean) =>
    api<{ ok: true }>(`/api/v1/tuya/app/accounts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { autoFetch },
    }),
  removeQrAccount: (id: string) =>
    api<{ ok: true }>(`/api/v1/tuya/sharing/accounts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  syncQrAccount: (id: string) =>
    api<{ sync: VysledokSynchronizacieUctu }>(
      `/api/v1/tuya/sharing/accounts/${encodeURIComponent(id)}/sync`,
      { method: 'POST' },
    ),
  /** Import po QR synchronizácii ide tou istou cestou ako pri účte aplikácie. */
  importQrDevices: (id: string, add: readonly string[], update: readonly string[] = []) =>
    api<{ import: { added: number; updated: number } }>(
      `/api/v1/tuya/app/accounts/${encodeURIComponent(id)}/import`,
      { method: 'POST', body: { add, update } },
    ),
  /** Uložené účty aplikácie Tuya; bez tajomstva a hesla. */
  appAccounts: (signal?: AbortSignal) =>
    api<{ accounts: UcetTuyaApp[] }>('/api/v1/tuya/app/accounts', signal ? { signal } : {}),
  addAppAccount: (input: NovyUcetTuyaApp) =>
    api<{ account: UcetTuyaApp }>('/api/v1/tuya/app/accounts', { method: 'POST', body: input }),
  removeAppAccount: (id: string) =>
    api<{ ok: true }>(`/api/v1/tuya/app/accounts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** Porovná účet s bránou; nič nezapisuje a kľúče nevracia. */
  syncAppAccount: (id: string) =>
    api<{ sync: VysledokSynchronizacieUctu }>(
      `/api/v1/tuya/app/accounts/${encodeURIComponent(id)}/sync`,
      { method: 'POST' },
    ),
  /** Zápis potvrdeného výberu z poslednej synchronizácie. */
  importAppDevices: (id: string, add: readonly string[], update: readonly string[] = []) =>
    api<{ import: { added: number; updated: number } }>(
      `/api/v1/tuya/app/accounts/${encodeURIComponent(id)}/import`,
      { method: 'POST', body: { add, update } },
    ),
};

export type UrovenLogu = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

export interface ZaznamLogu {
  readonly seq: number;
  readonly time: string;
  readonly level: UrovenLogu;
  readonly msg: string;
  readonly context: Readonly<Record<string, unknown>>;
}

export interface ZaznamAuditu {
  readonly id: number;
  readonly at: string;
  readonly action: string;
  readonly actor: string;
  readonly subject: string | null;
  readonly detail: string | null;
}

export const audit = {
  list: (action?: string, signal?: AbortSignal) => {
    const otaznik = action === undefined ? '' : `?action=${encodeURIComponent(action)}`;
    return api<{ entries: ZaznamAuditu[] }>(`/api/v1/audit${otaznik}`, signal ? { signal } : {});
  },
};

export const logs = {
  list: (
    query: { level?: UrovenLogu; search?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) => {
    const parametre = new URLSearchParams();
    if (query.level) parametre.set('level', query.level);
    if (query.search !== undefined && query.search !== '') parametre.set('search', query.search);
    if (query.limit !== undefined) parametre.set('limit', String(query.limit));
    const otaznik = parametre.size > 0 ? `?${parametre.toString()}` : '';
    return api<{ logs: ZaznamLogu[] }>(`/api/v1/logs${otaznik}`, signal ? { signal } : {});
  },
};

export const integrations = {
  list: (signal?: AbortSignal) =>
    api<{ integrations: Integracia[] }>('/api/v1/integrations', signal ? { signal } : {}),
};

export interface NovyMiniserver {
  readonly name: string;
  readonly host: string;
  readonly port?: number;
  readonly udpPort?: number;
  readonly username: string;
  readonly password: string;
  readonly configVersion?: string;
  readonly useTls?: boolean;
}

export const loxone = {
  miniservers: (signal?: AbortSignal) =>
    api<{ miniservers: Miniserver[] }>('/api/v1/loxone/miniservers', signal ? { signal } : {}),
  mappings: (signal?: AbortSignal) =>
    api<{ mappings: Mapovanie[] }>('/api/v1/loxone/mappings', signal ? { signal } : {}),
  check: (id: string) =>
    api<{ check: KontrolaImportu }>(`/api/v1/loxone/miniservers/${encodeURIComponent(id)}/check`, {
      method: 'POST',
    }),
  pair: (input: NovyMiniserver) =>
    api<{ miniserver: { id: string; name: string } }>('/api/v1/loxone/miniservers', {
      method: 'POST',
      body: input,
    }),
  unpair: (id: string) =>
    api<{ ok: true }>(`/api/v1/loxone/miniservers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  createMapping: (deviceId: string, miniserverId: string, transport?: Mapovanie['transport']) =>
    api<{ mapping: Mapovanie; bundle: Balik }>('/api/v1/loxone/mappings', {
      method: 'POST',
      body: { deviceId, miniserverId, ...(transport ? { transport } : {}) },
    }),
  /**
   * Všetky šablóny naraz ako ZIP, rozdelené do `VirtualIn` a `VirtualOut`.
   *
   * Pri zmene tvaru šablóny treba naimportovať všetko znova — jedno
   * rozbalenie do priečinkov Configu namiesto klikania cez každé
   * zariadenie zvlášť.
   */
  vsetkySablony: () =>
    api<{ fileName: string; base64: string; count: number }>('/api/v1/loxone/templates.zip'),
  removeMapping: (id: string) =>
    api<{ ok: true }>(`/api/v1/loxone/mappings/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** Sablona je POST, lebo obsahuje parovaci token a patri len spravcovi. */
  template: (mappingId: string) =>
    api<{ bundle: Balik }>(`/api/v1/loxone/mappings/${encodeURIComponent(mappingId)}/template`, {
      method: 'POST',
    }),
};
